import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../lib/api';
import type { CaseExpenseStatus, CaseExpenseSummary } from '../types';

const STATUSES: CaseExpenseStatus[] = ['pending', 'paid'];

export function CaseExpenseModal({
  caseId,
  initial,
  onSaved,
  onClose,
  onDeleted,
}: {
  caseId: string;
  initial?: CaseExpenseSummary | null;
  onSaved: () => void;
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const { t } = useTranslation(['cases', 'common']);
  const editing = !!initial;

  const [description, setDescription] = useState(initial?.description ?? '');
  const [amountIls, setAmountIls] = useState(
    initial ? (initial.amountAgorot / 100).toFixed(2) : '',
  );
  const [incurredAt, setIncurredAt] = useState(() =>
    toDateInput(initial?.incurredAt ?? new Date().toISOString()),
  );
  const [status, setStatus] = useState<CaseExpenseStatus>(initial?.status ?? 'pending');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const amount = Number(amountIls);
      if (!Number.isFinite(amount) || amount < 0) {
        setError(t('expense_modal.error_amount'));
        setBusy(false);
        return;
      }
      const body = {
        description: description.trim(),
        amountAgorot: Math.round(amount * 100),
        incurredAt: new Date(incurredAt).toISOString(),
        status,
      };
      if (editing && initial) {
        await apiFetch(`/api/cases/${caseId}/expenses/${initial.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      } else {
        await apiFetch(`/api/cases/${caseId}/expenses`, {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : err instanceof Error ? err.message : 'unknown');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!editing || !initial) return;
    if (!confirm(t('expense_modal.delete_confirm'))) return;
    setBusy(true);
    try {
      await apiFetch(`/api/cases/${caseId}/expenses/${initial.id}`, { method: 'DELETE' });
      onDeleted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'delete_failed');
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-card modal-card" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="form-card-header">
            <div style={{ flex: 1 }}>
              <div className="form-preview-name">
                {editing ? t('expense_modal.title_edit') : t('expense_modal.title_new')}
              </div>
            </div>
            <button
              type="button"
              className="modal-close-btn"
              onClick={onClose}
              aria-label={t('common:actions.close')}
            >
              ×
            </button>
          </div>

          <div className="form-section">
            <div className="field-grid">
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>{t('expense_modal.field_description')} <span className="req">*</span></label>
                <input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="field">
                <label>{t('expense_modal.field_amount')} <span className="req">*</span></label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min={0}
                  value={amountIls}
                  onChange={(e) => setAmountIls(e.target.value)}
                  required
                />
              </div>
              <div className="field">
                <label>{t('expense_modal.field_incurred_at')} <span className="req">*</span></label>
                <input
                  type="date"
                  value={incurredAt}
                  onChange={(e) => setIncurredAt(e.target.value)}
                  required
                />
              </div>
              <div className="field">
                <label>{t('expense_modal.field_status')}</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as CaseExpenseStatus)}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {t(`expense_statuses.${s}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {error && (
            <div className="form-section">
              <div className="signin-error">{error}</div>
            </div>
          )}

          <div className="form-footer">
            <button type="submit" className="btn-save" disabled={busy}>
              {busy ? t('common:actions.loading_dots') : t('common:actions.save')}
            </button>
            <button type="button" className="btn-cancel" onClick={onClose} disabled={busy}>
              {t('common:actions.cancel')}
            </button>
            {editing && onDeleted && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy}
                className="btn-danger"
                style={{ marginInlineStart: 'auto' }}
              >
                {t('common:actions.delete')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function toDateInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
